import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";

// Lazy load pages for code splitting
const Landing = lazy(() => import("./pages/Landing"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Guest Pages
const GuestHome = lazy(() => import("./pages/guest/Home"));
const RSVP = lazy(() => import("./pages/guest/RSVP"));
const Logistics = lazy(() => import("./pages/guest/Logistics"));
const Itinerary = lazy(() => import("./pages/guest/Itinerary"));
const Updates = lazy(() => import("./pages/guest/Updates"));
const Venue = lazy(() => import("./pages/guest/Venue"));
const Info = lazy(() => import("./pages/guest/Info"));

// Admin Pages
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminRegister = lazy(() => import("./pages/admin/Register"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminFamilies = lazy(() => import("./pages/admin/Families"));
const AdminGuestPipeline = lazy(() => import("./pages/admin/GuestPipeline"));
const AdminLogistics = lazy(() => import("./pages/admin/LogisticsManagement"));
const AdminEvents = lazy(() => import("./pages/admin/Events"));
const AdminUpdates = lazy(() => import("./pages/admin/UpdatesManagement"));
const AdminTasks = lazy(() => import("./pages/admin/Tasks"));
const AdminExpenses = lazy(() => import("./pages/admin/Expenses"));
const AdminVendors = lazy(() => import("./pages/admin/Vendors"));
const AdminBroadcast = lazy(() => import("./pages/admin/Broadcast"));
const AdminGuestMessages = lazy(() => import("./pages/admin/GuestMessages"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminGuestDocuments = lazy(() => import("./pages/admin/GuestDocuments"));
const AdminGalleryUpload = lazy(() => import("./pages/admin/GalleryUpload"));
const AdminBulkImport = lazy(() => import("./pages/admin/BulkImport"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false, // Prevent refetch storms on mobile idle resume
    },
  },
});

// Loading Fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="w-8 h-8 text-primary animate-spin" />
      <p className="text-sm text-muted-foreground animate-pulse">Loading experience...</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public landing */}
              <Route path="/" element={<Landing />} />

              {/* Guest routes (token-based) */}
              <Route path="/home" element={<GuestHome />} />
              <Route path="/rsvp" element={<RSVP />} />
              <Route path="/logistics" element={<Logistics />} />
              <Route path="/itinerary" element={<Itinerary />} />
              <Route path="/updates" element={<Updates />} />
              <Route path="/venue" element={<Venue />} />
              <Route path="/info" element={<Info />} />

              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/register" element={<AdminRegister />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/families" element={<AdminFamilies />} />
              <Route path="/admin/guests" element={<AdminGuestPipeline />} />
              <Route path="/admin/logistics" element={<AdminLogistics />} />
              <Route path="/admin/events" element={<AdminEvents />} />
              <Route path="/admin/updates" element={<AdminUpdates />} />
              <Route path="/admin/tasks" element={<AdminTasks />} />
              <Route path="/admin/expenses" element={<AdminExpenses />} />
              <Route path="/admin/vendors" element={<AdminVendors />} />
              <Route path="/admin/broadcast" element={<AdminBroadcast />} />
              <Route path="/admin/messages" element={<AdminGuestMessages />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/documents" element={<AdminGuestDocuments />} />
              <Route path="/admin/gallery" element={<AdminGalleryUpload />} />
              <Route path="/admin/import" element={<AdminBulkImport />} />

              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
